import { cn } from '@/shared/lib';
import { useRTL } from '@/shared/hooks';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/shared/components';
import { ClockIcon, MapIcon, PackageIcon } from 'lucide-react';
import { OrderPointDto, OrderPointPVZType } from '@/app/store/api/restApi';

interface OrderPointsInfoCardProps {
  isMinimalistic?: boolean;
  selectedOrderPoint: OrderPointDto;
  selectedPointsArray: OrderPointDto[];
  setSelectedOrderPoint: (orderPoint: OrderPointDto | null) => void;
}

export const OrderPointsInfoCard: FC<OrderPointsInfoCardProps> = ({
  isMinimalistic,
  selectedOrderPoint,
  selectedPointsArray,
  setSelectedOrderPoint,
}) => {
  const isRTL = useRTL();
  const { t: tAddress } = useTranslation('address');

  const getOrderNumber = useCallback(
    (point: OrderPointDto, index: number) => {
      const sameTypePrecedingPoints = selectedPointsArray.slice(0, index).filter((p) => p.pvzType === point.pvzType);
      return sameTypePrecedingPoints.length + 1;
    },
    [selectedPointsArray],
  );

  const weightText = useMemo(() => {
    const { weightMinKg, weightMaxKg } = selectedOrderPoint;

    if (weightMinKg != null && weightMaxKg != null)
      return tAddress('weight.range', { min: weightMinKg, max: weightMaxKg });
    if (weightMinKg != null) return tAddress('weight.min', { min: weightMinKg });
    if (weightMaxKg != null) return tAddress('weight.max', { max: weightMaxKg });

    return undefined;
  }, [selectedOrderPoint, tAddress]);

  return (
    <div
      className={cn(
        'absolute bottom-0 left-[-16px] bg-background border-t border-t-solid border-t-border w-[calc(100%+32px)] flex flex-col gap-4 p-4 pb-3',
        !isMinimalistic && 'rounded-t-[10px]',
      )}
    >
      {selectedPointsArray.length > 1 && (
        <div className='flex p-1 overflow-x-auto'>
          {selectedPointsArray.map((point, index) => {
            const displayName =
              point.pvzType === OrderPointPVZType.Pvz
                ? tAddress('pickupPointType.orderPoint')
                : tAddress('pickupPointType.postamat');

            const orderNum = getOrderNumber(point, index);

            return (
              <div
                key={point.id}
                onClick={() => setSelectedOrderPoint(point)}
                className={cn(
                  'flex flex-1 h-6 items-center justify-center cursor-pointer border-b border-solid border-transparent',
                  selectedOrderPoint.id === point.id && 'border-primary',
                )}
              >
                <Typography
                  variant='text5'
                  color={selectedOrderPoint.id === point.id ? 'primary' : 'mutedForeground'}
                  className='px-2 min-w-23 text-center whitespace-nowrap'
                >
                  {isRTL ? `${orderNum} ${displayName}` : `${displayName} ${orderNum}`}
                </Typography>
              </div>
            );
          })}
        </div>
      )}

      <Typography variant='p' className='font-semibold'>
        {selectedOrderPoint.pvzType === OrderPointPVZType.Pvz
          ? tAddress('pickupPointType.orderPointCdek')
          : tAddress('pickupPointType.postamatCdek')}
      </Typography>
      <Typography variant='subtitle1' color='cardForeground' className='flex gap-2'>
        <MapIcon size={20} className='text-primary' />
        {selectedOrderPoint.location.address.city + ', ' + selectedOrderPoint.location.address.short}
      </Typography>
      <Typography variant='subtitle1' className='flex gap-2'>
        <ClockIcon size={20} className='text-primary' />
        {selectedOrderPoint.scheduleString}
      </Typography>
      {weightText && (
        <Typography variant='subtitle1' className='flex gap-2'>
          <PackageIcon size={20} className='text-primary' />
          {weightText}
        </Typography>
      )}
    </div>
  );
};
